// Feed optimization utilities for lite mode performance
import { useConnectionMode } from '@/connection/SimpleConnectionProvider';

// Determine optimal feed configuration based on connection mode
export const useFeedOptimization = () => {
  const mode = useConnectionMode();
  
  const isLiteMode = mode === 'lite' || mode === 'offline';
  
  return {
    // Data fetching optimization
    pageSize: isLiteMode ? 10 : 20,
    prefetchNext: !isLiteMode,
    enableRealtime: mode === 'full',
    
    // UI rendering optimization  
    enableAnimations: mode !== 'lite',
    enableHoverEffects: mode === 'full',
    showFullAvatars: mode !== 'lite',
    showUserBadges: mode !== 'lite',
    
    // Media optimization
    imageQuality: isLiteMode ? 'low' : 'auto',
    enableVideoAutoplay: mode === 'full',
    videoMaxQuality: isLiteMode ? '360p' : '1080p',
    
    // Performance optimization
    batchReactions: true,
    cacheTimeout: isLiteMode ? 120000 : 60000, // 2min for lite, 1min for full
    enableVirtualization: isLiteMode,
    
    // Network optimization
    enableServiceWorker: isLiteMode,
    enableOfflineCache: isLiteMode,
    compressRequests: isLiteMode,
    
    // Feature toggles
    enableInlineEditing: mode === 'full',
    enableQuickActions: mode !== 'offline',
    showDetailedMetrics: mode === 'full',
    
    // CSS class helpers
    getContainerClass: () => isLiteMode ? 'lite-feed-container' : 'full-feed-container',
    getItemClass: () => isLiteMode ? 'lite-feed-item' : 'full-feed-item',
    getImageClass: () => isLiteMode ? 'lite-image' : 'full-image'
  };
};

// Feed item data transformation for lite mode
export const optimizeFeedItem = (item: any, isLiteMode: boolean) => {
  if (!isLiteMode) return item;
  
  // Strip unnecessary data in lite mode
  const optimized = {
    id: item.id,
    type: item.type,
    content: item.content?.substring(0, 280) || item.iso_request?.substring(0, 280), // Truncate long content
    author_name: item.author_name,
    author_avatar: item.author_avatar,
    likes_count: item.likes_count,
    comments_count: item.comments_count || item.responses_count,
    created_at: item.created_at,
    sentiment: item.sentiment,
    isLiked: item.isLiked,
    // Include only essential location info
    location_address: item.location_address,
    // Remove heavy fields in lite mode
    ...(item.image_url && !isLiteMode && { image_url: item.image_url }),
    ...(item.solved !== undefined && { solved: item.solved })
  };
  
  return optimized;
};

// Batch reaction queue manager
export class ReactionQueue {
  private queue: Array<{
    id: string;
    type: 'like' | 'unlike';
    target_type: 'post' | 'cast';
    target_id: string;
    timestamp: number;
  }> = [];
  
  private flushTimer: NodeJS.Timeout | null = null;
  private maxBatchSize = 10;
  private flushInterval = 2000; // 2 seconds
  
  constructor(private onFlush: (reactions: any[]) => Promise<void>) {}
  
  add(reaction: { type: 'like' | 'unlike'; target_type: 'post' | 'cast'; target_id: string }) {
    // Remove any existing reaction for the same item to avoid conflicts
    this.queue = this.queue.filter(r => r.target_id !== reaction.target_id);
    
    // Add new reaction
    this.queue.push({
      id: `${reaction.target_id}-${Date.now()}`,
      ...reaction,
      timestamp: Date.now()
    });
    
    console.log(`📦 Reaction queued: ${reaction.type} ${reaction.target_type}:${reaction.target_id}`);
    
    // Auto-flush if batch is full or after interval
    if (this.queue.length >= this.maxBatchSize) {
      this.flush();
    } else {
      this.scheduleFlush();
    }
  }
  
  private scheduleFlush() {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
    }
    
    this.flushTimer = setTimeout(() => {
      this.flush();
    }, this.flushInterval);
  }
  
  async flush() {
    if (this.queue.length === 0) return;
    
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    
    const reactions = [...this.queue];
    this.queue = [];
    
    console.log(`⚡ Flushing ${reactions.length} reactions`);
    
    try {
      await this.onFlush(reactions);
      console.log('✅ Reactions flushed successfully');
    } catch (error) {
      console.error('❌ Failed to flush reactions:', error);
      // Re-queue failed reactions
      this.queue = [...reactions, ...this.queue];
    }
  }
  
  getPendingCount() {
    return this.queue.length;
  }
  
  clear() {
    this.queue = [];
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }
}

// Image optimization helpers
export const getOptimizedImageUrl = (url: string | undefined, isLiteMode: boolean): string | undefined => {
  if (!url) return undefined;
  
  // For Cloudinary URLs, add optimization parameters
  if (url.includes('cloudinary') || url.includes('res.cloudinary.com')) {
    const quality = isLiteMode ? 'q_auto:low' : 'q_auto';
    const format = 'f_auto';
    const optimization = isLiteMode ? 'fl_lossy' : '';
    
    // Insert optimization parameters
    const params = [quality, format, optimization].filter(Boolean).join(',');
    
    if (url.includes('/upload/') && !url.includes('q_auto')) {
      return url.replace('/upload/', `/upload/${params}/`);
    }
  }
  
  return url;
};

// Video optimization helpers  
export const getVideoOptimization = (isLiteMode: boolean) => ({
  autoplay: !isLiteMode,
  preload: isLiteMode ? 'none' : 'metadata',
  quality: isLiteMode ? '360p' : 'auto',
  controls: true,
  muted: true, // Required for autoplay
  playsInline: true,
  // Lite mode specific optimizations
  ...(isLiteMode && {
    poster: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjI0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjY2NjIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMThweCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkNsaWNrIHRvIFBsYXk8L3RleHQ+PC9zdmc+',
    'data-lite-optimized': 'true'
  })
});

// Performance monitoring
export const trackFeedPerformance = (metrics: {
  loadTime: number;
  itemCount: number;
  isLiteMode: boolean;
  cacheHit: boolean;
}) => {
  console.log(`📊 Feed Performance:`, metrics);
  
  // In production, send to analytics
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', 'feed_load', {
      load_time: metrics.loadTime,
      item_count: metrics.itemCount,
      is_lite_mode: metrics.isLiteMode,
      cache_hit: metrics.cacheHit
    });
  }
};